import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Plane, Clock, MapPin, Phone } from "lucide-react";

const LINES = ["SHIP", "WITH", "EASE."];
const AIR_IMG =
  "https://images.unsplash.com/photo-1571086291540-b137111fa1c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxhaXIlMjBjYXJnbyUyMHBsYW5lfGVufDB8fHx8MTc4Njk2MjA2NXww&ixlib=rb-4.1.0&q=85";

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const { scrollY } = useScroll();
  const yImage = useTransform(scrollY, [0, 700], [0, -90]);
  const yCard = useTransform(scrollY, [0, 700], [0, -160]);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden"
    >
      <div
        className="absolute -top-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-[#FF8A00]/10 blur-[120px]"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-0 -left-52 w-[30rem] h-[30rem] rounded-full bg-[#5B2A86]/30 blur-[120px]"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center w-full">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="inline-flex items-center gap-2 border border-white/15 rounded-full px-4 py-1.5 mb-8"
            data-testid="hero-badge"
          >
            <Plane className="w-3.5 h-3.5 text-[#FF8A00]" />
            <span className="font-mono-accent text-xs tracking-widest uppercase text-[#A29DB0]">
              Linstead · Jamaica — Air &amp; Ocean Freight
            </span>
          </motion.div>

          <h1 className="font-display font-900 tracking-tighter leading-[0.92] text-6xl sm:text-7xl lg:text-8xl">
            {LINES.map((line, i) => (
              <span className="mask-line" key={line}>
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 0.9,
                    delay: 0.25 + i * 0.14,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className={i === 2 ? "text-[#FF8A00]" : ""}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-8 max-w-md text-base sm:text-lg text-[#A29DB0] leading-relaxed"
            data-testid="hero-subtext"
          >
            Seamless door-to-door package shipping and customs clearance.
            Shop online anywhere in the world — we bring it home to Jamaica.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-signup-cta"
              href="https://becc.takumiwhm.com/auth/register?tenantHost=becc.takumiwhm.com&returnUrl=https%3A%2F%2Fbecc.takumiwhm.com"
              target="_blank"
              rel="noreferrer"
              className="group px-7 py-3.5 rounded-full bg-[#FF8A00] text-[#12071F] font-semibold hover:bg-[#FF9F2E] transition-colors duration-200 flex items-center gap-2"
            >
              Get Your Free Mailbox
              <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <button
              data-testid="hero-rates-cta"
              onClick={() => scrollTo("rates")}
              className="px-7 py-3.5 rounded-full border border-white/20 hover:bg-white/10 transition-colors duration-200"
            >
              See Rates
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-white/10 pt-6"
            data-testid="hero-info-bar"
          >
            <div className="flex items-center gap-2.5 text-sm text-[#A29DB0]">
              <Clock className="w-4 h-4 text-[#FF8A00] shrink-0" />
              Mon–Sat · 9:00am–5:30pm
            </div>
            <a
              data-testid="hero-phone-link"
              href="tel:+18763555819"
              className="flex items-center gap-2.5 text-sm text-[#A29DB0] hover:text-white transition-colors duration-200"
            >
              <Phone className="w-4 h-4 text-[#FF8A00] shrink-0" />
              (876) 355-5819
            </a>
            <div className="flex items-center gap-2.5 text-sm text-[#A29DB0]">
              <MapPin className="w-4 h-4 text-[#FF8A00] shrink-0" />
              South Parade Plaza, Linstead
            </div>
          </motion.div>
        </div>

        <div className="relative hidden lg:block" data-testid="hero-visual">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: yImage }}
            className="relative rounded-3xl overflow-hidden border border-white/10 inner-glow aspect-[4/5]"
          >
            <img
              src={AIR_IMG}
              alt="Air freight cargo aircraft"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#12071F]/30" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            style={{ y: yCard }}
            className="absolute -bottom-8 -left-10 bg-[#1C0D2C]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-5 flex items-center gap-4 animate-float-slow"
            data-testid="hero-floating-card"
          >
            <img
              src="/bec-logo.jpg"
              alt="BEC Couriers"
              className="w-12 h-12 rounded-full object-cover ring-1 ring-[#FF8A00]/40"
            />
            <div>
              <p className="font-display font-700 text-sm">clearly BEC Couriers</p>
              <p className="font-mono-accent text-xs text-[#FF8A00] tracking-widest uppercase">
                Ship With Ease
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
